import { Alert } from '@mui/material';
import {
    FormAutocomplete,
    FormText,
    FormInputGroup,
    FormSelect,
    FormSection,
} from 'components/form';
import { FormSelectOption } from 'components/form/types';
import { useAuth } from 'hooks';
import {
    convertDeliveryStatusToInternalName,
    deliveryStatusDisplayNames,
    ticketTriggerOptions,
} from 'lib/constants';
import React, { useState } from 'react';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import { DeviceLookup } from 'views/Planning/Planning';
import { useFormContext } from 'react-hook-form';
import { OfflineLedSequences } from 'views/DeviceDetails/modals/OfflineLedSequences/OfflineLedSequences';

export interface AddIssueFormProps {
    supplierId?: string;
    userOptions: FormSelectOption[];
    devicesList: DeviceLookup;
    setDevicesList: React.Dispatch<React.SetStateAction<DeviceLookup>>;
}
export const AddIssueForm = ({
    userOptions,
    supplierId,
    devicesList,
    setDevicesList,
}: AddIssueFormProps) => {
    const { watch } = useFormContext();
    const issueType = watch('ticketTrigger');
    const deviceId = watch('taggedDevices');

    const { user } = useAuth();

    const [searchDeviceQuery, { isLoading }] =
        useLazyGetSupplierDevicesEsQuery();

    const [deviceOptions, setDeviceOptions] = useState<FormSelectOption[]>([]);
    const [searchDebounceId, setSearchDebounceId] =
        useState<NodeJS.Timeout | null>(null);

    const onSearchChange = (searchTerm: string) => {
        if (searchDebounceId !== null) clearTimeout(searchDebounceId);
        if (searchTerm) {
            setSearchDebounceId(
                setTimeout(() => {
                    searchDevices(searchTerm);
                }, 500)
            );
        }
    };

    const searchDevices = (searchTerm: string) => {
        if (user && supplierId) {
            const queryConfig: ElasticsearchDeviceBodyConfig = {
                limit: 10,
                search: searchTerm,
                sortField: 'name',
                searchFields: ['name'],
                isAdminUser: user.protected?.supplierId.toString() === '0',
            };
            searchDeviceQuery({
                supplierId,
                userUid: user.uid,
                queryConfig,
            }).then((result) => {
                if (result?.data?.results) {
                    setDeviceOptions(
                        result.data.results.map((device) => ({
                            name: device.name,
                            value: device.deviceId,
                        }))
                    );
                    const newDevicesList = {
                        ...devicesList,
                    };
                    result.data.results.forEach((device) => {
                        newDevicesList[device.deviceId] = device;
                    });

                    setDevicesList(newDevicesList);
                }
            });
        }
    };

    return (
        <>
            <FormSection title="Details">
                <FormInputGroup>
                    <FormText field="name" label="Ticket Name" />
                    <FormAutocomplete
                        label="Tank"
                        field="taggedDevices"
                        options={[
                            { name: 'Please select a tank', value: '' },
                            ...deviceOptions,
                        ]}
                        onSearch={(searchTerm) => onSearchChange(searchTerm)}
                        isLoading={isLoading}
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormText field="description" label="Description" />
                    <FormSelect
                        label="Ticket Type"
                        field="ticketTrigger"
                        options={ticketTriggerOptions}
                    />
                </FormInputGroup>
                <FormInputGroup>
                    <FormAutocomplete
                        label="Tagged User"
                        field="taggedUsers"
                        options={[
                            { name: 'Please select a user', value: '' },
                            ...userOptions,
                        ]}
                    />
                    <FormSelect
                        label="Status"
                        field="status"
                        options={deliveryStatusDisplayNames.map((status) => ({
                            name: status,
                            value: convertDeliveryStatusToInternalName[status],
                        }))}
                    />
                </FormInputGroup>
            </FormSection>
            <FormSection title="Troubleshooting">
                {issueType === 'offline' ? (
                    <OfflineLedSequences hasUnknownOption />
                ) : (
                    <Alert severity="info">
                        {' '}
                        This type of issue does not have troubleshooting steps.
                        Please proceed to the next tab.
                    </Alert>
                )}
            </FormSection>
            <FormSection title="Confirm">
                <Alert severity="info">
                    Tank selected:{' '}
                    <strong>
                        {deviceOptions?.find((d) => d.value === deviceId)?.name}
                    </strong>{' '}
                    — Issue selected: <strong>{issueType}</strong>
                </Alert>
            </FormSection>
        </>
    );
};
